import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import Nav from '../components/Nav';
import Scorecard from '../components/Scorecard';

export default function Dashboard() {
  const nav = useNavigate();
  const [calls, setCalls] = useState([]);
  const [usage, setUsage] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.getCalls(), api.getUsage()])
      .then(([c, u]) => { setCalls(c); setUsage(u); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function fmt(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function fmtDur(s) {
    if (!s) return '—';
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  }

  function avgScore(fb) {
    if (!fb || !fb.scores) return null;
    const keys = Object.keys(fb.scores).filter(k => fb.scores[k] && typeof fb.scores[k].score === 'number');
    if (keys.length === 0) return null;
    return (keys.reduce((a, k) => a + fb.scores[k].score, 0) / keys.length).toFixed(1);
  }

  function scoreColor(s) {
    if (!s) return '#9CA3AF';
    return +s >= 7 ? '#16A34A' : +s >= 5 ? '#D97706' : '#DC2626';
  }

  const callsUsed = usage?.calls_this_month || 0;
  const callsLimit = usage?.calls_limit || 3;

  return (
    <div style={S.page}>
      <Nav />
      <div style={S.content}>

        {/* Usage card */}
        {usage && (
          <div style={S.usageCard}>
            <div style={S.usageTop}>
              <div>
                <div style={S.usageTitle}>
                  {usage.plan === 'beta' ? '🎉 Beta Plan' : '⭐ Student Plan'}
                </div>
                <div style={S.usageSub}>
                  <strong>{usage.calls_remaining}</strong> of {callsLimit} practice calls remaining this month
                </div>
              </div>
              <div style={S.usagePills}>
                <span style={S.pill}>Max {usage.max_call_duration_minutes} min/call</span>
                <span style={S.pill}>Up to {usage.max_scenarios} scenarios</span>
                <button style={S.practiceNowBtn} onClick={() => nav('/practice')}>Practice now →</button>
              </div>
            </div>
            <div style={S.usageBarBg}>
              <div style={{ ...S.usageBarFill, width: `${(callsUsed / callsLimit) * 100}%`, background: callsUsed >= callsLimit ? '#EF4444' : '#2563EB' }} />
            </div>
          </div>
        )}

        {/* Call history */}
        <div style={S.sectionHeader}>
          <div style={S.sectionTitle}>Practice History</div>
          {calls.length > 0 && <div style={S.sectionSub}>{calls.length} calls recorded</div>}
        </div>

        {loading && <div style={S.empty}>Loading your calls...</div>}

        {!loading && calls.length === 0 && (
          <div style={S.emptyState}>
            <div style={S.emptyIcon}>🎙️</div>
            <div style={S.emptyTitle}>No practice calls yet</div>
            <div style={S.emptyBody}>Make your first practice call to see your scorecard here.</div>
            <button style={S.emptyBtn} onClick={() => nav('/practice')}>Start your first call →</button>
          </div>
        )}

        <div style={S.callList}>
          {calls.map(call => {
            const score = avgScore(call.feedback);
            const isOpen = expanded === call.id;
            return (
              <div key={call.id} style={S.callCard}>
                <div style={S.callHeader} onClick={() => setExpanded(isOpen ? null : call.id)}>
                  <div style={S.callMeta}>
                    <span style={S.scenarioBadge}>{call.scenario_name || 'Unknown scenario'}</span>
                    <span style={S.callTime}>{fmt(call.started_at)}</span>
                    <span style={S.callDur}>{fmtDur(call.duration_seconds)}</span>
                    <span style={S.turnCount}>{call.turns.length} turns</span>
                    {score && (
                      <span style={{ ...S.scoreChip, color: scoreColor(score), background: +score >= 7 ? '#F0FDF4' : +score >= 5 ? '#FFFBEB' : '#FEF2F2', borderColor: +score >= 7 ? '#BBF7D0' : +score >= 5 ? '#FDE68A' : '#FECACA' }}>
                        {score}/10
                      </span>
                    )}
                  </div>
                  <span style={S.chevron}>{isOpen ? '▲' : '▼'}</span>
                </div>

                {isOpen && (
                  <div>
                    {call.feedback
                      ? <Scorecard feedback={call.feedback} mode={call.scenario_mode || 'sales'} />
                      : <div style={S.pendingFeedback}>Feedback is being generated — refresh in a moment.</div>
                    }
                    <div style={S.transcriptSection}>
                      <div style={S.transcriptLabel}>Transcript</div>
                      {call.turns.map((t, i) => (
                        <div key={i} style={S.turn}>
                          <span style={{ ...S.turnWho, color: t.speaker === 'user' ? '#2563EB' : '#9CA3AF' }}>
                            {t.speaker === 'user' ? 'YOU' : 'AGENT'}
                          </span>
                          <span style={S.turnText}>{t.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', background: '#F8F9FB', fontFamily: 'Inter, -apple-system, sans-serif' },
  content: { maxWidth: 900, margin: '0 auto', padding: '32px 24px' },
  usageCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, padding: '20px 24px', marginBottom: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  usageTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 12 },
  usageTitle: { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 },
  usageSub: { fontSize: 14, color: '#6B7280' },
  usagePills: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  pill: { fontSize: 12, padding: '4px 10px', background: '#F1F3F7', border: '1px solid #E5E7EB', borderRadius: 999, color: '#6B7280' },
  practiceNowBtn: { fontSize: 13, fontWeight: 600, color: '#fff', background: '#2563EB', border: 'none', borderRadius: 8, padding: '7px 16px', cursor: 'pointer' },
  usageBarBg: { height: 6, background: '#F1F3F7', borderRadius: 3, overflow: 'hidden' },
  usageBarFill: { height: '100%', borderRadius: 3, transition: 'width 0.4s' },
  sectionHeader: { display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#111827' },
  sectionSub: { fontSize: 13, color: '#9CA3AF' },
  empty: { color: '#9CA3AF', fontSize: 14, padding: '16px 0' },
  emptyState: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, padding: '48px 24px', textAlign: 'center' },
  emptyIcon: { fontSize: 40, marginBottom: 16 },
  emptyTitle: { fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8 },
  emptyBody: { fontSize: 14, color: '#9CA3AF', marginBottom: 24 },
  emptyBtn: { padding: '10px 24px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  callList: { display: 'flex', flexDirection: 'column', gap: 10 },
  callCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  callHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', cursor: 'pointer' },
  callMeta: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  scenarioBadge: { fontSize: 12, fontWeight: 600, padding: '3px 10px', background: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE', borderRadius: 999 },
  callTime: { fontSize: 13, color: '#9CA3AF' },
  callDur: { fontSize: 13, color: '#D1D5DB' },
  turnCount: { fontSize: 13, color: '#D1D5DB' },
  scoreChip: { fontSize: 13, fontWeight: 700, padding: '3px 10px', border: '1px solid', borderRadius: 999, fontFamily: 'JetBrains Mono, monospace' },
  chevron: { fontSize: 11, color: '#D1D5DB' },
  pendingFeedback: { padding: '16px 24px', fontSize: 13, color: '#9CA3AF', fontStyle: 'italic', borderBottom: '1px solid #E5E7EB' },
  transcriptSection: { padding: '16px 24px', background: '#F8F9FB' },
  transcriptLabel: { fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 },
  turn: { display: 'flex', gap: 10, marginBottom: 8, fontSize: 13, lineHeight: 1.5 },
  turnWho: { fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', flexShrink: 0, width: 44, paddingTop: 2 },
  turnText: { color: '#374151' },
};
